import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Plus, AlertTriangle, Cpu, Trash2, CircleDollarSign,
  ListChecks, Table2, ShieldAlert, CheckCircle2, ImageOff, Building2, Pencil, X
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/auth-hook';
import { toAbsoluteFotoUrl } from '@/utils/foto-url';
import { podeAcessarVendas } from './vendas-acesso';
import { ImportarArsModal } from './ImportarArsModal';
import { PainelPrecificacao, DadosParte } from './PainelPrecificacao';
import { TabelaFinalView } from './TabelaFinalView';
import {
  Cotacao, CotacaoMaquina, StatusCotacao, TabelaFinal,
  CLASSE_STATUS_COTACAO, ROTULO_STATUS_COTACAO, moeda,
} from './cotacao-types';

type Aba = 'precificar' | 'tabela';

export function CotacaoWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const temAcesso = podeAcessarVendas(user?.squadNome, isAdmin);

  const [cotacao, setCotacao] = useState<Cotacao | null>(null);
  const [tabela, setTabela] = useState<TabelaFinal | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [aba, setAba] = useState<Aba>('precificar');
  const [maquinaSelecionadaId, setMaquinaSelecionadaId] = useState<string | null>(null);
  const [mostrarImportar, setMostrarImportar] = useState(false);
  const [editandoDados, setEditandoDados] = useState(false);

  const carregar = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiService.obterCotacao(id);
      if (res.success && res.data) {
        const dados = res.data as Cotacao;
        setCotacao(dados);
        setMaquinaSelecionadaId((atual) =>
          atual && dados.maquinas.some((m) => m.id === atual) ? atual : dados.maquinas[0]?.id ?? null
        );
        setErro(null);
      } else {
        setErro(res.message || 'Cotação não encontrada.');
      }
    } catch {
      setErro('Não foi possível carregar a cotação.');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    if (!temAcesso) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    carregar();
  }, [carregar, temAcesso]);

  // A tabela final é derivada no servidor; só buscamos quando a aba está aberta,
  // e refazemos sempre que a cotação muda.
  useEffect(() => {
    if (aba !== 'tabela' || !id || !cotacao) return;
    let cancelado = false;
    apiService.obterTabelaCotacao(id).then((res) => {
      if (!cancelado && res.success) setTabela(res.data as TabelaFinal);
    });
    return () => { cancelado = true; };
  }, [aba, id, cotacao]);

  const maquina = useMemo(
    () => cotacao?.maquinas.find((m) => m.id === maquinaSelecionadaId) ?? null,
    [cotacao, maquinaSelecionadaId]
  );

  /**
   * Aplica a resposta do servidor sobre a máquina em edição e recalcula os
   * totais localmente, para o painel reagir na hora sem recarregar a cotação
   * inteira a cada valor digitado.
   */
  const aplicarLocal = useCallback((transformar: (m: CotacaoMaquina) => CotacaoMaquina) => {
    setCotacao((atual) => {
      if (!atual) return atual;
      const maquinas = atual.maquinas.map((m) => (m.id === maquinaSelecionadaId ? recalcular(transformar(m)) : m));
      return { ...atual, maquinas, totais: somarTotais(maquinas) };
    });
  }, [maquinaSelecionadaId]);

  const salvarParte = useCallback(
    async (parteId: string, dados: DadosParte) => {
      const res = await apiService.atualizarParteCotacao(parteId, dados);
      if (!res.success) {
        setErro(res.message || 'Não foi possível salvar o valor.');
        return false;
      }
      const atualizada = res.data;
      aplicarLocal((m) => ({
        ...m,
        partes: m.partes.map((p) => (p.id === parteId ? { ...p, ...atualizada } : p)),
      }));
      setErro(null);
      return true;
    },
    [aplicarLocal]
  );

  const salvarMaquina = useCallback(
    async (dados: { modoValor?: 'parte' | 'unico'; valorUnico?: number | null }) => {
      if (!maquinaSelecionadaId) return false;
      const res = await apiService.atualizarMaquinaCotacao(maquinaSelecionadaId, dados);
      if (!res.success) {
        setErro(res.message || 'Não foi possível salvar a máquina.');
        return false;
      }
      aplicarLocal((m) => ({ ...m, ...res.data }));
      setErro(null);
      return true;
    },
    [aplicarLocal, maquinaSelecionadaId]
  );

  const removerMaquina = async (alvo: CotacaoMaquina) => {
    const confirmado = window.confirm(
      `Remover "${alvo.nome}" desta cotação?\n\nAs ${alvo.resumo.totalPartes} partes e os valores lançados serão apagados.`
    );
    if (!confirmado) return;
    const res = await apiService.removerMaquinaCotacao(alvo.id);
    if (res.success) {
      setMaquinaSelecionadaId(null);
      carregar();
    } else {
      setErro(res.message || 'Não foi possível remover a máquina.');
    }
  };

  const alterarStatus = async (status: StatusCotacao) => {
    if (!id) return;
    const res = await apiService.atualizarCotacao(id, { status });
    if (res.success) setCotacao((atual) => (atual ? { ...atual, status } : atual));
    else setErro(res.message || 'Não foi possível mudar a situação.');
  };

  const exportar = async (formato: 'xlsx' | 'pdf') => {
    if (!id) return;
    try {
      await apiService.exportarCotacao(id, formato);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível exportar.');
    }
  };

  if (!temAcesso) {
    return (
      <div className="p-8 text-center">
        <ShieldAlert className="h-10 w-10 mx-auto text-schmersal-gray-medium mb-3" />
        <p className="font-semibold text-schmersal-blue dark:text-white">Área restrita ao squad de Vendas.</p>
      </div>
    );
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-24 text-schmersal-gray-medium">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Carregando cotação...
      </div>
    );
  }

  if (!cotacao) {
    return (
      <div className="p-8 text-center">
        <p className="font-semibold text-schmersal-blue dark:text-white mb-2">{erro ?? 'Cotação não encontrada.'}</p>
        <Link to="/vendas/cotacoes" className="text-sm text-schmersal-blue dark:text-schmersal-turquoise hover:underline">
          Voltar para a lista
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <button
            onClick={() => navigate('/vendas/cotacoes')}
            className="flex items-center gap-1.5 text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:text-schmersal-blue mb-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Cotações
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs px-2 py-0.5 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-schmersal-gray-light">
              {cotacao.numero}
            </span>
            <select
              value={cotacao.status}
              onChange={(e) => alterarStatus(e.target.value as StatusCotacao)}
              className={`text-xs px-2 py-0.5 rounded border font-semibold cursor-pointer bg-transparent ${CLASSE_STATUS_COTACAO[cotacao.status]}`}
            >
              {(Object.keys(ROTULO_STATUS_COTACAO) as StatusCotacao[]).map((s) => (
                <option key={s} value={s} className="text-schmersal-gray-dark">{ROTULO_STATUS_COTACAO[s]}</option>
              ))}
            </select>
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-schmersal-blue dark:text-white mt-1 flex items-center gap-2">
            {cotacao.titulo}
            <button
              onClick={() => setEditandoDados(true)}
              title="Editar dados da cotação"
              className="p-1 text-schmersal-gray-medium hover:text-schmersal-blue dark:hover:text-schmersal-turquoise"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </h1>
          {(cotacao.cliente || cotacao.obra) && (
            <p className="text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light flex items-center gap-1 mt-0.5">
              <Building2 className="h-3 w-3" />
              {[cotacao.cliente, cotacao.obra].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-schmersal-gray-medium flex items-center gap-1 justify-end">
              <CircleDollarSign className="h-3 w-3" /> valor total
            </div>
            <div className="text-2xl font-bold text-schmersal-blue dark:text-schmersal-turquoise">
              {moeda(cotacao.totais.valorTotal)}
            </div>
            {cotacao.totais.pendentes > 0 && (
              <div className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                {cotacao.totais.pendentes} parte(s) sem preço
              </div>
            )}
          </div>
          <button
            onClick={() => setMostrarImportar(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-schmersal-blue hover:bg-schmersal-blue-light text-white rounded-lg font-semibold text-sm transition-colors"
          >
            <Plus className="h-4 w-4" /> Importar ARs
          </button>
        </div>
      </div>

      {erro && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{erro}</span>
          <button onClick={() => setErro(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="flex gap-1 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark mb-5">
        <BotaoAba ativo={aba === 'precificar'} onClick={() => setAba('precificar')} icone={ListChecks}>
          Precificar
        </BotaoAba>
        <BotaoAba ativo={aba === 'tabela'} onClick={() => setAba('tabela')} icone={Table2}>
          Tabela final
        </BotaoAba>
      </div>

      {cotacao.maquinas.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-schmersal-gray-darker rounded-xl">
          <Cpu className="h-10 w-10 mx-auto text-schmersal-gray-medium mb-3" />
          <p className="font-semibold text-schmersal-blue dark:text-white">Nenhuma máquina nesta cotação</p>
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-1 mb-4">
            Importe as Apreciações de Risco em PDF. Cada uma vira uma máquina com fotos e pontos já classificados.
          </p>
          <button
            onClick={() => setMostrarImportar(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-schmersal-blue hover:bg-schmersal-blue-light text-white rounded-lg font-semibold text-sm"
          >
            <Plus className="h-4 w-4" /> Importar ARs
          </button>
        </div>
      ) : aba === 'tabela' ? (
        tabela ? (
          <TabelaFinalView tabela={tabela} onExportar={exportar} />
        ) : (
          <div className="flex items-center justify-center py-16 text-schmersal-gray-medium">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Montando a tabela...
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-5">
          <ListaMaquinas
            maquinas={cotacao.maquinas}
            selecionadaId={maquinaSelecionadaId}
            onSelecionar={setMaquinaSelecionadaId}
            onRemover={removerMaquina}
          />
          {maquina ? (
            <PainelPrecificacao
              maquina={maquina}
              onSalvarParte={salvarParte}
              onSalvarMaquina={salvarMaquina}
            />
          ) : (
            <div className="flex items-center justify-center py-16 text-schmersal-gray-medium bg-white dark:bg-schmersal-gray-darker rounded-xl">
              Selecione uma máquina à esquerda.
            </div>
          )}
        </div>
      )}

      {mostrarImportar && id && (
        <ImportarArsModal
          cotacaoId={id}
          onFechar={() => setMostrarImportar(false)}
          onImportou={carregar}
        />
      )}

      {editandoDados && id && (
        <ModalDadosCotacao
          cotacao={cotacao}
          onFechar={() => setEditandoDados(false)}
          onSalvo={(dados) => {
            setCotacao((atual) => (atual ? { ...atual, ...dados } : atual));
            setEditandoDados(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ListaMaquinas({
  maquinas, selecionadaId, onSelecionar, onRemover,
}: {
  maquinas: CotacaoMaquina[];
  selecionadaId: string | null;
  onSelecionar: (id: string) => void;
  onRemover: (m: CotacaoMaquina) => void;
}) {
  const [busca, setBusca] = useState('');

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return maquinas;
    return maquinas.filter((m) =>
      [m.nome, m.tag, m.setor].filter(Boolean).some((c) => String(c).toLowerCase().includes(termo))
    );
  }, [maquinas, busca]);

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-xl p-3 h-fit lg:sticky lg:top-4">
      <input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder={`Filtrar ${maquinas.length} máquina(s)`}
        className="w-full px-3 py-2 mb-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
      />
      <div className="space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
        {filtradas.map((m) => {
          const ativa = m.id === selecionadaId;
          return (
            <div
              key={m.id}
              className={`group flex items-center gap-2 rounded-lg px-2 py-2 cursor-pointer transition-colors ${
                ativa ? 'bg-schmersal-blue text-white' : 'hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark'
              }`}
              onClick={() => onSelecionar(m.id)}
            >
              {m.fotos[0] ? (
                <img src={toAbsoluteFotoUrl(m.fotos[0].url)} alt="" loading="lazy" className="w-9 h-9 rounded object-cover flex-shrink-0" />
              ) : (
                <span className="w-9 h-9 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex items-center justify-center flex-shrink-0">
                  <ImageOff className="h-3.5 w-3.5 text-schmersal-gray-medium" />
                </span>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${ativa ? 'text-white' : 'text-schmersal-gray-dark dark:text-white'}`}>
                  {m.nome}
                </p>
                <p className={`text-[11px] flex items-center gap-1 ${ativa ? 'text-white/80' : 'text-schmersal-gray-medium'}`}>
                  {m.resumo.concluida ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <AlertTriangle className="h-3 w-3" />
                  )}
                  {m.resumo.concluida
                    ? moeda(m.resumo.total)
                    : `${m.resumo.pendentes} de ${m.resumo.naoAdequadas} sem preço`}
                </p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onRemover(m); }}
                title="Remover máquina"
                className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                  ativa ? 'text-white/70 hover:text-white' : 'text-schmersal-gray-medium hover:text-red-600'
                }`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
        {filtradas.length === 0 && (
          <p className="text-xs text-schmersal-gray-medium text-center py-4">Nenhuma máquina para este filtro.</p>
        )}
      </div>
    </div>
  );
}

function ModalDadosCotacao({
  cotacao, onFechar, onSalvo,
}: {
  cotacao: Cotacao;
  onFechar: () => void;
  onSalvo: (dados: Partial<Cotacao>) => void;
}) {
  const [titulo, setTitulo] = useState(cotacao.titulo);
  const [cliente, setCliente] = useState(cotacao.cliente ?? '');
  const [obra, setObra] = useState(cotacao.obra ?? '');
  const [validadeDias, setValidadeDias] = useState(String(cotacao.validadeDias));
  const [observacoes, setObservacoes] = useState(cotacao.observacoes ?? '');
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!titulo.trim()) return;
    setSalvando(true);
    const dados = {
      titulo: titulo.trim(),
      cliente: cliente.trim(),
      obra: obra.trim(),
      validadeDias: Number(validadeDias) || 30,
      observacoes: observacoes.trim(),
    };
    const res = await apiService.atualizarCotacao(cotacao.id, dados);
    setSalvando(false);
    if (res.success) {
      onSalvo({
        titulo: dados.titulo,
        cliente: dados.cliente || null,
        obra: dados.obra || null,
        validadeDias: dados.validadeDias,
        observacoes: dados.observacoes || null,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md bg-white dark:bg-schmersal-gray-darker rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <h2 className="font-bold text-schmersal-blue dark:text-white">Dados da cotação</h2>
          <button onClick={onFechar} className="p-1 text-schmersal-gray-medium hover:text-schmersal-blue">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <CampoTexto rotulo="Título *" valor={titulo} onChange={setTitulo} />
          <CampoTexto rotulo="Cliente" valor={cliente} onChange={setCliente} />
          <CampoTexto rotulo="Obra / planta" valor={obra} onChange={setObra} />
          <CampoTexto rotulo="Validade (dias)" valor={validadeDias} onChange={setValidadeDias} tipo="number" />
          <label className="block">
            <span className="block text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light mb-1">
              Observações da proposta
            </span>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue resize-none"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <button onClick={onFechar} className="px-4 py-2 text-sm font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:underline">
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={!titulo.trim() || salvando}
            className="flex items-center gap-2 px-4 py-2 bg-schmersal-blue hover:bg-schmersal-blue-light disabled:opacity-50 text-white rounded-lg font-semibold text-sm"
          >
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function CampoTexto({
  rotulo, valor, onChange, tipo = 'text',
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  tipo?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light mb-1">{rotulo}</span>
      <input
        type={tipo}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
      />
    </label>
  );
}

function BotaoAba({
  ativo, onClick, icone: Icone, children,
}: {
  ativo: boolean;
  onClick: () => void;
  icone: typeof ListChecks;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
        ativo
          ? 'border-schmersal-turquoise text-schmersal-blue dark:text-white'
          : 'border-transparent text-schmersal-gray-medium hover:text-schmersal-blue'
      }`}
    >
      <Icone className="h-4 w-4" />
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Espelho local dos cálculos do backend (cotacao.service.ts), para o painel
// responder na hora. A verdade continua vindo do servidor no próximo carregamento.

function recalcular(maquina: CotacaoMaquina): CotacaoMaquina {
  const partes = maquina.partes.map((p) => ({
    ...p,
    valorEfetivo: p.status === 'sem_custo' || p.status === 'nao_aplicavel' ? 0 : p.valor ?? 0,
  }));
  const naoAdequadas = partes.filter((p) => !p.adequada);
  const somaPartes = naoAdequadas.reduce((s, p) => s + p.valorEfetivo, 0);
  const pendentes = naoAdequadas.filter((p) => p.status === 'pendente').length;
  const total = maquina.modoValor === 'unico' ? maquina.valorUnico ?? 0 : somaPartes;

  return {
    ...maquina,
    partes,
    resumo: {
      totalPartes: partes.length,
      adequadas: partes.length - naoAdequadas.length,
      naoAdequadas: naoAdequadas.length,
      pendentes,
      cotadas: naoAdequadas.length - pendentes,
      somaPartes,
      total,
      concluida: maquina.modoValor === 'unico' ? maquina.valorUnico !== null : pendentes === 0,
    },
  };
}

function somarTotais(maquinas: CotacaoMaquina[]) {
  return maquinas.reduce(
    (acc, m) => ({
      maquinas: acc.maquinas + 1,
      partes: acc.partes + m.resumo.totalPartes,
      adequadas: acc.adequadas + m.resumo.adequadas,
      naoAdequadas: acc.naoAdequadas + m.resumo.naoAdequadas,
      pendentes: acc.pendentes + m.resumo.pendentes,
      valorTotal: acc.valorTotal + m.resumo.total,
    }),
    { maquinas: 0, partes: 0, adequadas: 0, naoAdequadas: 0, pendentes: 0, valorTotal: 0 }
  );
}
